import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Mail,
  Plus,
  Copy,
  Share2,
  Check,
  Heart,
  Calendar,
  MapPin,
  Sparkles,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvitationsPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, getAuthHeader } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [copied, setCopied] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    couple_names: '',
    wedding_date: '',
    venue: '',
    message: '',
    rsvp_deadline: '',
  });

  useEffect(() => {
    fetchTemplates();
    if (isAuthenticated) {
      fetchInvitations();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/invitations/templates`);
      setTemplates(response.data.templates || []);
    } catch (error) {
      toast.error('Failed to load templates');
    }
  };

  const fetchInvitations = async () => {
    try {
      const response = await axios.get(`${API}/invitations`, { headers: getAuthHeader() });
      setInvitations(response.data || []);
    } catch (error) {
      toast.error('Failed to load invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvitation = async () => {
    if (!selectedTemplate || !formData.couple_names || !formData.wedding_date || !formData.venue) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const response = await axios.post(`${API}/invitations`, {
        template_id: selectedTemplate.id,
        title: formData.title || `${formData.couple_names}'s Wedding`,
        ...formData
      }, { headers: getAuthHeader() });

      setInvitations([...invitations, response.data]);
      setCreateOpen(false);
      setSelectedTemplate(null);
      setFormData({
        title: '',
        couple_names: '',
        wedding_date: '',
        venue: '',
        message: '',
        rsvp_deadline: '',
      });
      toast.success('Invitation created!');
    } catch (error) {
      toast.error('Failed to create invitation');
    }
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link);
    setCopied(link);
    setTimeout(() => setCopied(null), 2000);
    toast.success('Link copied!');
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50/50 to-white">
        {/* Header */}
        <div className="bg-white border-b border-pink-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <h1 className="font-heading text-4xl lg:text-5xl font-bold mb-4">
                E-<span className="gradient-text">Invitations</span>
              </h1>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Create beautiful digital wedding invitations to share with your guests
              </p>
            </motion.div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Templates Preview */}
          <h2 className="font-heading text-2xl font-bold mb-6">Choose a Template</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
            {templates.map((template) => (
              <motion.div
                key={template.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="group cursor-pointer"
              >
                <div className="aspect-[3/4] rounded-2xl overflow-hidden border-2 border-pink-100 hover:border-pink-300 transition-all">
                  <img
                    src={template.preview}
                    alt={template.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  />
                </div>
                <p className="text-sm font-medium text-center mt-2">{template.name}</p>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center py-12 bg-white rounded-3xl border border-pink-100">
            <div className="w-20 h-20 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="w-10 h-10 text-pink-500" />
            </div>
            <h2 className="font-heading text-2xl font-bold mb-4">Create Your Invitation</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Sign up to create personalized digital invitations and share them with your guests.
            </p>
            <Button onClick={() => navigate('/register')} className="btn-primary" data-testid="invitations-register-btn">
              Get Started Free
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50/50 to-white">
      {/* Header */}
      <div className="bg-white border-b border-pink-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-3xl lg:text-4xl font-bold mb-2">
                E-<span className="gradient-text">Invitations</span>
              </h1>
              <p className="text-muted-foreground">Create and manage your digital wedding invitations</p>
            </div>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary gap-2" data-testid="create-invitation-btn">
                  <Plus className="w-4 h-4" />
                  Create Invitation
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create New Invitation</DialogTitle>
                </DialogHeader>
                <div className="space-y-6 pt-4">
                  {/* Template Selection */}
                  <div>
                    <label className="text-sm font-medium mb-3 block">Choose Template</label>
                    <div className="grid grid-cols-3 gap-3">
                      {templates.map((template) => (
                        <button
                          key={template.id}
                          onClick={() => setSelectedTemplate(template)}
                          className={`aspect-[3/4] rounded-xl overflow-hidden border-2 transition-all ${
                            selectedTemplate?.id === template.id
                              ? 'border-pink-500 ring-2 ring-pink-200'
                              : 'border-pink-100 hover:border-pink-300'
                          }`}
                        >
                          <img
                            src={template.preview}
                            alt={template.name}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Form Fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="text-sm font-medium mb-2 block">Couple Names *</label>
                      <Input
                        placeholder="e.g., Priya & Rahul"
                        value={formData.couple_names}
                        onChange={(e) => setFormData({ ...formData, couple_names: e.target.value })}
                        className="rounded-xl"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Wedding Date *</label>
                      <Input
                        type="date"
                        value={formData.wedding_date}
                        onChange={(e) => setFormData({ ...formData, wedding_date: e.target.value })}
                        className="rounded-xl"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">RSVP Deadline</label>
                      <Input
                        type="date"
                        value={formData.rsvp_deadline}
                        onChange={(e) => setFormData({ ...formData, rsvp_deadline: e.target.value })}
                        className="rounded-xl"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-sm font-medium mb-2 block">Venue *</label>
                      <Input
                        placeholder="e.g., Royal Banquet Hall, Toronto"
                        value={formData.venue}
                        onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                        className="rounded-xl"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-sm font-medium mb-2 block">Personal Message</label>
                      <Textarea
                        placeholder="Add a personal message for your guests..."
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="rounded-xl"
                        rows={3}
                      />
                    </div>
                  </div>

                  <Button onClick={handleCreateInvitation} className="w-full btn-primary">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Create Invitation
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* My Invitations */}
        <h2 className="font-heading text-xl font-bold mb-6">My Invitations</h2>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={`inv-skel-${i}`} className="h-64 bg-pink-50 rounded-3xl animate-pulse" />
            ))}
          </div>
        ) : invitations.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-3xl border border-pink-100">
            <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-pink-300" />
            </div>
            <h3 className="font-heading font-bold text-xl mb-2">No invitations yet</h3>
            <p className="text-muted-foreground mb-6">Create your first digital invitation!</p>
            <Button onClick={() => setCreateOpen(true)} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Create Invitation
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {invitations.map((invitation) => {
              const template = templates.find(t => t.id === invitation.template_id);
              return (
                <motion.div
                  key={invitation.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-3xl overflow-hidden border border-pink-100"
                  data-testid={`invitation-card-${invitation.id}`}
                >
                  <div className="aspect-[16/10] relative">
                    <img
                      src={template?.preview || 'https://images.unsplash.com/photo-1519741497674-611481863552?w=400'}
                      alt={invitation.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-4">
                      <div className="text-white">
                        <p className="font-accent text-2xl">{invitation.couple_names}</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                      <Calendar className="w-4 h-4" />
                      {new Date(invitation.wedding_date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                      <MapPin className="w-4 h-4" />
                      {invitation.venue}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopyLink(invitation.share_link)}
                        className="flex-1 rounded-full border-pink-200"
                      >
                        {copied === invitation.share_link ? (
                          <Check className="w-4 h-4 mr-1" />
                        ) : (
                          <Copy className="w-4 h-4 mr-1" />
                        )}
                        Copy Link
                      </Button>
                      <Button size="sm" className="flex-1 rounded-full bg-pink-50 text-pink-600 hover:bg-pink-100">
                        <Share2 className="w-4 h-4 mr-1" />
                        Share
                      </Button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Templates Section */}
        <div className="mt-16">
          <h2 className="font-heading text-xl font-bold mb-6">Available Templates</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {templates.map((template) => (
              <div key={template.id} className="group">
                <div className="aspect-[3/4] rounded-2xl overflow-hidden border border-pink-100 group-hover:border-pink-300 transition-all">
                  <img
                    src={template.preview}
                    alt={template.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  />
                </div>
                <p className="text-sm font-medium text-center mt-2">{template.name}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvitationsPage;
